import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, FileText, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, zmw, dt, openFile } from "@/lib/api";
import { useAuth } from "@/context/Auth";
import { PageHeader, Card, Loading, StatusBadge, Table, useLoad, act } from "@/components/common";
import { AddPerson, AdminActions } from "@/pages/Staff";

export const FIELD_LABELS = { full_name: "Full names", email: "Email", phone: "Phone", nrc_number: "NRC number", address: "Address", occupation: "Occupation", workplace: "Workplace",
  next_of_kin_name: "Next of kin name", next_of_kin_relationship: "Next of kin relationship", next_of_kin_phone: "Next of kin phone", nrc_file_id: "NRC copy", photo_file_id: "Profile photo" };
const TYPES = [["all", "Everyone"], ["investor", "Investors"], ["village", "Village banking members"], ["borrower", "Borrowers"], ["credit_agent", "Credit agents"], ["finance", "Finance staff"], ["super_admin", "Super admins"], ["applicant", "Applicants"]];
const STATUSES = [["all", "Any status"], ["waiting", "Waiting for check"], ["verified", "Verified"], ["more_info", "More info requested"], ["rejected", "Rejected"], ["unverified", "Not submitted yet"]];
const ROLE_NAMES = { member: "Investor", borrower: "Borrower", credit_agent: "Credit agent", finance: "Finance", super_admin: "Super admin", applicant: "Applicant" };

const isWaiting = (u) => u.verification_status === "pending" || u.pending_change?.status === "pending";
const matchType = (u, t) => t === "all" || (t === "investor" ? u.roles.includes("member") : t === "village" ? u.group_count > 0 : u.roles.includes(t));
const matchStatus = (u, s) => s === "all" || (s === "waiting" ? isWaiting(u) : u.verification_status === s || (s === "unverified" && !u.verification_status));
const typeBadges = (u) => [...u.roles.map((r) => ROLE_NAMES[r] || r), ...(u.group_count ? ["Village banking"] : [])];

const STAFF_ROLES = ["credit_agent", "finance", "super_admin"];
const isCustomer = (u) => u.roles.some((r) => !STAFF_ROLES.includes(r));

export function Customers() { return <Users mode="customers" />; }

export function Users({ mode = "checks" }) {
  const [sp] = useSearchParams();
  const { can } = useAuth();
  const { data: raw, reload } = useLoad("/staff/users");
  const all = raw && (mode === "customers" ? raw.filter(isCustomer) : raw);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [status, setStatus] = useState(sp.get("status") === "pending" || mode === "checks" ? "waiting" : "all");
  const [open, setOpen] = useState(null);
  const rows = useMemo(() => (all || []).filter((u) => {
    const s = q.trim().toLowerCase();
    const hay = [u.full_name, u.email, u.phone, u.profile?.nrc_number].join(" ").toLowerCase();
    return (!s || hay.includes(s)) && matchType(u, type) && matchStatus(u, status);
  }), [all, q, type, status]);
  if (!all) return <Loading />;
  const types = mode === "customers" ? TYPES.filter(([t]) => !["credit_agent", "finance", "super_admin"].includes(t)) : TYPES;
  const counts = Object.fromEntries(types.map(([t]) => [t, all.filter((u) => matchType(u, t)).length]));
  return (
    <div className="space-y-6">
      {mode === "customers" ? <PageHeader overline="Customers" title="All customers" explain="Everyone who invests, borrows, saves in a village group or has applied. Search or filter, open a person to see their details, or delete someone whose balance is zero." />
      : <PageHeader overline="People" title="ID checks & staff" explain="Shows people waiting for an ID check first. Change the status filter to see everyone. Open a person to see all their details and documents, then Approve, Reject or Request more details. Your comment is sent to them.">
        {can("manage_settings") && <AddPerson onDone={reload} />}
      </PageHeader>}
      <div className="flex flex-wrap gap-2" data-testid="people-type-chips">
        {types.map(([t, l]) => (
          <button key={t} onClick={() => setType(t)} data-testid={`people-type-${t}`}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium ring-1 transition-colors ${type === t ? "bg-[#0369a1] text-white ring-[#0369a1]" : "bg-white text-stone-700 ring-stone-200 hover:bg-sky-50"}`}>
            {l} <span className="opacity-70">({counts[t]})</span>
          </button>
        ))}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1"><Search className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
          <Input className="pl-9" placeholder="Search name, email, phone or NRC" value={q} onChange={(e) => setQ(e.target.value)} data-testid="people-search" /></div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="sm:w-60" data-testid="people-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`people-status-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Card testid="users-table">
        <p className="mb-3 text-xs text-stone-500" data-testid="people-count">Showing {rows.length} of {all.length} people</p>
        <Table rows={rows} empty="No one matches these filters." cols={[
          { k: "full_name", h: "Name", r: (u) => <div><b>{u.full_name}</b><div className="text-xs text-stone-500">{u.email}</div><div className="text-xs text-stone-500">{u.phone}</div></div> },
          { k: "roles", h: "Type", r: (u) => <div className="flex flex-wrap gap-1">{typeBadges(u).map((b) => <span key={b} className="rounded bg-sky-50 px-1.5 py-0.5 text-[11px] font-medium text-sky-800">{b}</span>)}</div> },
          { k: "nrc", h: "NRC", r: (u) => <span className="font-mono text-xs">{u.profile?.nrc_number || "—"}</span> },
          { k: "v", h: "ID check", r: (u) => <div className="space-y-1"><StatusBadge status={u.verification_status || "unverified"} />{u.pending_change?.status === "pending" && <div className="text-[11px] font-semibold text-amber-700">Details change waiting</div>}{u.status === "disabled" && <div className="text-[11px] text-rose-700">Access removed</div>}</div> },
          { k: "x", h: "", r: (u) => <Button size="sm" variant={isWaiting(u) ? "default" : "outline"} className={isWaiting(u) ? "bg-[#0369a1]" : ""} onClick={() => setOpen(u.id)} data-testid={`view-person-${u.id}`}><Eye className="mr-1 h-4 w-4" />{isWaiting(u) ? "Review" : "View"}</Button> }]} />
      </Card>
      <PersonSheet id={open} onClose={() => setOpen(null)} onChanged={reload} />
    </div>
  );
}

function Row({ label, value, next }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 border-b border-stone-100 py-1.5 text-sm">
      <div className="text-stone-500">{label}</div>
      <div>{value || "—"}{next !== undefined && <div className="mt-0.5 rounded bg-amber-50 px-1.5 py-0.5 text-amber-900" data-testid={`pending-${label}`}>New: <b>{next || "—"}</b></div>}</div>
    </div>
  );
}

function PersonSheet({ id, onClose, onChanged }) {
  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl" data-testid="person-sheet">
        {id && <PersonDetails id={id} onDone={() => { onChanged(); onClose(); }} />}
      </SheetContent>
    </Sheet>
  );
}

function PersonDetails({ id, onDone }) {
  const { can, user: me } = useAuth();
  const { data, reload } = useLoad(`/staff/users/${id}/details`);
  if (!data) return <Loading />;
  const u = data.user, p = u.profile || {}, pc = u.pending_change?.status === "pending" ? u.pending_change.fields : {};
  const val = (k) => (["email", "phone"].includes(k) ? u[k] : p[k]);
  const fileName = (fid) => data.files.find((f) => f.id === fid)?.original_filename || (fid ? "Uploaded file" : "");
  const show = (k) => (k.endsWith("_file_id") ? fileName(val(k)) : val(k));
  return (
    <div className="space-y-6 pb-10">
      <SheetHeader><SheetTitle className="font-heading text-2xl text-brand" data-testid="person-name">{u.full_name}</SheetTitle><SheetDescription className="sr-only">Personal details, documents and ID check decision</SheetDescription></SheetHeader>
      <div className="flex flex-wrap items-center gap-2"><StatusBadge status={u.verification_status || "unverified"} testid="person-verification-status" />{typeBadges({ ...u, group_count: data.groups.length }).map((b) => <span key={b} className="rounded bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800">{b}</span>)}{data.referred_by && <span className="text-xs text-stone-500">Brought in by {data.referred_by}</span>}</div>
      {Object.keys(pc).length > 0 && <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="pending-change-banner">This person changed {Object.keys(pc).length} detail(s) on {dt(u.pending_change.submitted_at)}. New values are shown in yellow. Their old details stay in use until you approve.</div>}
      <section><h3 className="mb-2 font-heading font-semibold text-brand">Personal details</h3>
        {Object.keys(FIELD_LABELS).filter((k) => !k.endsWith("_file_id")).map((k) => <Row key={k} label={FIELD_LABELS[k]} value={show(k)} next={k in pc ? pc[k] : undefined} />)}
        <Row label="Account status" value={u.status} /><Row label="Joined" value={dt(u.created_at)} />
      </section>
      <Documents data={data} pc={pc} p={p} />
      <Activity data={data} />
      <History u={u} />
      {can("verify_users") && (u.id !== me.id || me.roles.includes("super_admin")) && <Decision u={u} pc={pc} onDone={() => { reload(); onDone(); }} />}
      <AdminActions u={u} onDone={onDone} />
    </div>
  );
}

function Documents({ data, pc, p }) {
  return (
    <section data-testid="person-documents"><h3 className="mb-2 font-heading font-semibold text-brand">Uploaded documents</h3>
      {!data.files.length && <p className="text-sm text-stone-500">No documents uploaded yet.</p>}
      <div className="space-y-2">{data.files.map((f) => (
        <div key={f.id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
          <div className="flex items-center gap-2 text-sm"><FileText className="h-4 w-4 text-[#0369a1]" /><div><b>{{ nrc: "NRC copy", photo: "Profile photo", borrower_doc: "Borrower document" }[f.kind] || f.kind}</b><div className="text-xs text-stone-500">{f.original_filename} · {dt(f.created_at)}</div></div>
            {f.pending && <span className="rounded bg-amber-100 px-1.5 text-[11px] font-semibold text-amber-800">New — waiting</span>}{f.in_use && <span className="rounded bg-emerald-50 px-1.5 text-[11px] font-semibold text-emerald-800">Current</span>}</div>
          <Button size="sm" variant="outline" onClick={() => openFile(f.id)} data-testid={`open-doc-${f.id}`}>Open</Button>
        </div>))}
        {p.nrc_file_id === "SAMPLE" && <p className="text-xs text-stone-500">NRC: sample placeholder on file.</p>}
      </div>
    </section>
  );
}

function Activity({ data }) {
  const inv = data.investor;
  return (
    <section data-testid="person-activity"><h3 className="mb-2 font-heading font-semibold text-brand">On the platform</h3>
      {inv && <Row label="Investor money" value={`Available ${zmw(inv.available)} · Lent out ${zmw(inv.allocated)}`} />}
      <Row label="Village groups" value={data.groups.map((g) => `${g.name} (${g.role}, ${g.status})`).join("; ")} />
      <Row label="Loans" value={data.loans.map((l) => `${l.anon_id} ${zmw(l.principal_minor)} — ${l.status}`).join("; ")} />
      <Row label="Sign-up" value={data.applications.map((a) => `${a.account_type} — ${a.status}`).join("; ")} />
    </section>
  );
}

function History({ u }) {
  const h = [...(u.verification_history || [])].reverse();
  return (
    <section data-testid="person-history"><h3 className="mb-2 font-heading font-semibold text-brand">Check history</h3>
      {!h.length && <p className="text-sm text-stone-500">No checks yet.</p>}
      <ul className="space-y-2">{h.map((x, i) => <li key={i} className="rounded-lg bg-stone-50 p-2.5 text-sm"><StatusBadge status={x.decision} /> <span className="text-xs text-stone-500">{x.scope === "details_change" ? "Details change" : "ID check"} · {x.by_name} · {dt(x.at)}</span>{x.reason && <div className="mt-1 text-stone-700">“{x.reason}”</div>}</li>)}</ul>
    </section>
  );
}

function Decision({ u, pc, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const isChange = Object.keys(pc).length > 0;
  const go = async (decision) => {
    setBusy(true);
    const ok = await act(() => api.post(`/staff/users/${u.id}/verify`, { decision, reason: note }), { verify: "Approved — the person was told", reject: "Rejected — the person was told", more_info: "Request sent to the person" }[decision]);
    setBusy(false);
    if (ok) onDone();
  };
  return (
    <section className="rounded-xl border-2 border-sky-200 bg-sky-50/50 p-4" data-testid="person-decision">
      <h3 className="font-heading font-semibold text-brand">{isChange ? "Decide on the details change" : "Decide on the ID check"}</h3>
      <p className="mb-2 text-xs text-stone-600">A comment is needed for Reject and Request more details. It is sent to the person.</p>
      <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Your NRC photo is blurry — please upload a clearer one" data-testid="decision-comment" />
      <div className="mt-3 flex flex-wrap gap-2">
        <Button disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" onClick={() => go("verify")} data-testid="decision-approve">Approve</Button>
        <Button disabled={busy || note.trim().length < 3} variant="outline" onClick={() => go("more_info")} data-testid="decision-more-info">Request more details</Button>
        <Button disabled={busy || note.trim().length < 3} variant="outline" className="text-rose-700" onClick={() => go("reject")} data-testid="decision-reject">Reject</Button>
      </div>
    </section>
  );
}
